import { useRef, useState } from 'react';
import { TextInput } from './Field.tsx';
import { NavIcon } from './NavIcon.tsx';
import { normalizeStringList } from '../lib/normalizeStringList.ts';

type Row = { id: number; value: string };

/**
 * Controlled editor for a list of short strings: one text input per row, a
 * delete button per row, and a "+ add" button. Holds the raw typed rows in
 * local state (so trailing spaces and a freshly-added empty row survive while
 * the user types), and hands the normalized array to `onChange`.
 *
 * The rows are seeded once from `value`; later changes to the `value` prop are
 * ignored (the raw editing buffer is the source of truth). Callers that need to
 * re-seed from a new `value` should remount the editor (e.g. via a `key` prop).
 * In the admin Settings page the panel unmounts on tab switch, so this is a
 * non-issue there.
 */
export function StringListEditor({
  value,
  onChange,
  itemLabel = 'item',
  addLabel = 'Add item',
  placeholder,
  maxLength = 120,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  itemLabel?: string;
  addLabel?: string;
  placeholder?: string;
  maxLength?: number;
}) {
  // Stable per-row ids so React keys survive insertion/deletion (index keys
  // would let a deleted middle row steal a sibling's focus/cursor).
  const nextId = useRef(0);
  const makeRow = (v: string): Row => ({ id: nextId.current++, value: v });
  const [rows, setRows] = useState<Row[]>(() => (value.length === 0 ? [''] : value).map(makeRow));

  // Keep at least one (possibly empty) row so the editor never collapses to
  // just the add button. Empty rows normalize away, so persistence is
  // unaffected.
  function update(next: Row[]) {
    const safe = next.length === 0 ? [makeRow('')] : next;
    setRows(safe);
    onChange(normalizeStringList(safe.map((r) => r.value)));
  }

  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={row.id} className="flex items-center gap-2">
          <TextInput
            value={row.value}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={`${itemLabel} ${i + 1}`}
            onChange={(e) =>
              update(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
            }
          />
          <button
            type="button"
            aria-label={`Remove ${row.value.trim() || `${itemLabel} ${i + 1}`}`}
            onClick={() => update(rows.filter((r) => r.id !== row.id))}
            className="shrink-0 rounded-md p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-red-600"
          >
            <NavIcon name="trash" size={18} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => update([...rows, makeRow('')])}
        className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
      >
        <span aria-hidden="true" className="text-base leading-none">
          +
        </span>
        {addLabel}
      </button>
    </div>
  );
}
